import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import InfoText from 'components/shared/info-text.component';
import { createPriceRequestTemplate, priceRequestCounterpartsTemplate } from 'apollo/templates/price-request';
import { getActionSetsTemplate } from 'apollo/templates/action-sets/query.graphql';
import { PriceRequest, PriceRequestCounterpart } from 'models/price-request';
import { errorMessageKey } from './price-request-format';
import CounterpartAutocomplete from './counterpart-autocomplete.component';

type NewPriceRequestDialogProps = {
    open: boolean;
    /** Chains pick the action set the request writes into; labs never see one. */
    isChain: boolean;
    initialCounterpartId?: string;
    onClose: () => void;
    onCreated: (request: PriceRequest) => void;
};

const MAX_NOTE_LENGTH = 2000;

/**
 * DD-3697: open a request as a DRAFT with the other party. The products are narrowed on the
 * draft itself (it starts with every product on the agreement).
 */
const NewPriceRequestDialog: FC<NewPriceRequestDialogProps> = ({
    open,
    isChain,
    initialCounterpartId,
    onClose,
    onCreated,
}) => {
    const { t: translate } = useTranslation(['price-requests', 'common']);
    const [counterpartId, setCounterpartId] = useState(initialCounterpartId ?? '');
    const [actionSetId, setActionSetId] = useState('');
    const [note, setNote] = useState('');
    const [errorMessage, setErrorMessage] = useState<string>();

    const counterparts = useQuery<{ priceRequestCounterparts: PriceRequestCounterpart[] }>(
        priceRequestCounterpartsTemplate,
        { skip: !open, fetchPolicy: 'cache-and-network' },
    );
    const actionSets = useQuery<{ actionSets: { _id: string; title: string }[] }>(getActionSetsTemplate, {
        skip: !open || !isChain,
        fetchPolicy: 'cache-and-network',
    });

    const [createRequest, { loading: isCreating }] = useMutation<{ createPriceRequest: PriceRequest }>(
        createPriceRequestTemplate,
        {
            refetchQueries: ['priceRequests'],
            onCompleted: ({ createPriceRequest }) => onCreated(createPriceRequest),
            onError: (error) => setErrorMessage(translate(errorMessageKey(error))),
        },
    );

    const close = () => {
        setErrorMessage(undefined);
        setNote('');
        setActionSetId('');
        onClose();
    };

    const counterpartList = counterparts.data?.priceRequestCounterparts ?? [];
    const selected = counterpartList.find((counterpart) => counterpart._id === counterpartId);
    // The backend defaults to the chain's active action set; show that as the preselection.
    const actionSetList = actionSets.data?.actionSets ?? [];
    const activeActionSet = actionSetList.find((actionSet) => actionSet._id === selected?.activeActionSet);
    const shownActionSetId = actionSetId || activeActionSet?._id || '';

    const submit = () => {
        setErrorMessage(undefined);
        createRequest({
            variables: {
                input: {
                    counterpartId,
                    actionSetId: isChain && actionSetId ? actionSetId : undefined,
                    note: note.trim() || undefined,
                },
            },
        });
    };

    const renderBody = () => {
        if (counterparts.loading && !counterparts.data) return <CircularProgress size={24} />;
        if (counterparts.error) {
            return (
                <Alert
                    severity="error"
                    action={<Button onClick={() => counterparts.refetch()}>{translate('RETRY')}</Button>}
                >
                    {translate('ERROR_LOADING_COUNTERPARTS')}
                </Alert>
            );
        }
        if (!counterpartList.length) {
            return (
                <Alert severity="info">{translate(isChain ? 'NO_COUNTERPARTS_CHAIN' : 'NO_COUNTERPARTS_LAB')}</Alert>
            );
        }
        return (
            <>
                <InfoText text={translate(isChain ? 'NEW_REQUEST_INFO_CHAIN' : 'NEW_REQUEST_INFO_LAB')} />
                <Box mt={2}>
                    <CounterpartAutocomplete
                        label={translate(isChain ? 'LAB' : 'CHAIN')}
                        counterparts={counterpartList}
                        value={counterpartId}
                        onChange={(id) => {
                            setCounterpartId(id);
                            setActionSetId('');
                        }}
                        dataTest="new-price-request.counterpart"
                    />
                </Box>
                {isChain ? (
                    <FormControl fullWidth sx={{ mt: 2 }} disabled={!counterpartId}>
                        <InputLabel id="new-price-request-action-set">{translate('ACTION_SET')}</InputLabel>
                        <Select
                            labelId="new-price-request-action-set"
                            label={translate('ACTION_SET')}
                            value={shownActionSetId}
                            onChange={(event) => setActionSetId(event.target.value)}
                            data-test="new-price-request.action-set"
                        >
                            {actionSetList.map((actionSet) => (
                                <MenuItem key={actionSet._id} value={actionSet._id}>
                                    {actionSet.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : undefined}
                <TextField
                    sx={{ mt: 2 }}
                    fullWidth
                    multiline
                    minRows={3}
                    label={translate('NOTE')}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    inputProps={{ maxLength: MAX_NOTE_LENGTH, 'data-test': 'new-price-request.note' }}
                />
                {errorMessage ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                ) : undefined}
            </>
        );
    };

    return (
        <DHDialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth="sm"
            title={translate('NEW_REQUEST')}
            actions={
                <>
                    <Button onClick={close}>{translate('common:CANCEL')}</Button>
                    <Button
                        variant="contained"
                        disabled={!counterpartId || isCreating}
                        onClick={submit}
                        data-test="new-price-request.create"
                    >
                        {translate('CREATE_DRAFT')}
                    </Button>
                </>
            }
        >
            {renderBody()}
        </DHDialog>
    );
};

export default NewPriceRequestDialog;
